/**
 * Types for super admin API endpoints.
 */

// --- Company Types ---

export interface SuperAdminCompanyResponse {
  id: string;
  name: string;
  owner_name: string;
  email: string | null;
  cellphone: string | null;
  address_1: string | null;
  address_2: string | null;
  zip_code: string | null;
  city: string | null;
  country: string | null;
  bio: string | null;
  ai_context: string | null;
  docs_context: string | null;
  tva_number: string | null;
  kbis_number: string | null;
  feature_flags: Record<string, FeatureFlagValue> | null;
  is_active: boolean;
  user_count: number;
  created_at: string;
  updated_at: string;
}

export interface SuperAdminCompanyCreate {
  name: string;
  owner_name: string;
  email?: string | null;
  cellphone?: string | null;
  address_1?: string | null;
  address_2?: string | null;
  zip_code?: string | null;
  city?: string | null;
  country?: string | null;
  bio?: string | null;
  ai_context?: string | null;
  docs_context?: string | null;
  tva_number?: string | null;
  kbis_number?: string | null;
  feature_flags?: Record<string, FeatureFlagValue> | null;
}

export interface SuperAdminCompanyUpdate {
  name?: string;
  owner_name?: string;
  email?: string | null;
  cellphone?: string | null;
  address_1?: string | null;
  address_2?: string | null;
  zip_code?: string | null;
  city?: string | null;
  country?: string | null;
  bio?: string | null;
  ai_context?: string | null;
  docs_context?: string | null;
  tva_number?: string | null;
  kbis_number?: string | null;
  feature_flags?: Record<string, FeatureFlagValue> | null;
  is_active?: boolean;
}

export interface PaginatedCompaniesResponse {
  items: SuperAdminCompanyResponse[];
  total: number;
  page: number;
  size: number;
}

// --- User Types ---

export interface SuperAdminUserResponse {
  id: string;
  email: string;
  full_name: string | null;
  cellphone: string;
  language: string;
  company_id: string | null;
  company_name: string | null;
  company_permissions: number | null;
  feature_permissions: number;
  feature_flags: Record<string, FeatureFlagValue> | null;
  is_active: boolean;
  ai_context: string | null;
  created_at: string;
  updated_at: string;
}

export interface SuperAdminUserCreate {
  email: string;
  password: string;
  full_name?: string | null;
  cellphone: string;
  language?: string;
  company_id?: string | null;
  company_permissions?: number | null;
  feature_permissions?: number;
  feature_flags?: Record<string, FeatureFlagValue> | null;
}

export interface SuperAdminUserUpdate {
  email?: string;
  full_name?: string | null;
  cellphone?: string;
  language?: string;
  company_id?: string | null;
  company_permissions?: number | null;
  feature_permissions?: number;
  feature_flags?: Record<string, FeatureFlagValue> | null;
  is_active?: boolean;
  ai_context?: string | null;
}

export interface PaginatedUsersResponse {
  items: SuperAdminUserResponse[];
  total: number;
  page: number;
  size: number;
}

// --- Employee Types ---

export interface SuperAdminEmployeeResponse {
  id: string;
  company_id: string;
  company_name: string | null;
  name: string;
  code: string;
  user_id: string | null;
  user_email: string | null;
  user_name: string | null;
  created_at: string;
  updated_at: string;
}

export interface SuperAdminEmployeeCreate {
  company_id: string;
  name: string;
  code?: string | null;
}

export interface SuperAdminEmployeeUpdate {
  name?: string;
  code?: string | null;
}

export interface SuperAdminEmployeeLinkRequest {
  user_id: string;
}

export interface PaginatedEmployeesResponse {
  items: SuperAdminEmployeeResponse[];
  total: number;
  page: number;
  size: number;
}

// --- Impersonation Types ---

export interface ImpersonationResponse {
  access_token: string;
  token_type: string;
  impersonated_user_id: string;
  impersonated_user_email: string;
  expires_in: number;
}

// --- Feature Flags Types ---

export type FeatureFlagType = "bool" | "str" | "int" | "list" | "dict";
export type FeatureFlagKind = "flag" | "config";
export type FeatureFlagScope = "company" | "user" | "both";

export interface FeatureFlagDefinition {
  name: string;
  default_value: FeatureFlagValue;
  description: string | null;
  type: FeatureFlagType | null;
  category: string | null;
  /** "flag" = capability/integration toggle, "config" = operational tuning. */
  kind: FeatureFlagKind | null;
  scope: FeatureFlagScope | null;
  allowed_values: string[] | null;
  /** For list flags, whether items are plain strings or JSON objects. */
  list_item: "string" | "object" | null;
  deprecated: boolean;
  deprecated_hint: string | null;
}

// List flags can hold either plain strings or structured JSON objects (e.g.
// custom-field definitions), so the array element type stays `unknown`. Dict
// flags (e.g. aiFieldRestrictions) hold a JSON object.
export type FeatureFlagValue =
  | boolean
  | string
  | number
  | unknown[]
  | Record<string, unknown>
  | null;

export interface BulkFeatureFlagUpdate {
  flags: Record<string, FeatureFlagValue>;
}

export interface FeatureFlagsResponse {
  flags: Record<string, FeatureFlagValue>;
}

// --- Statistics Types ---

export interface SuperAdminStats {
  total_companies: number;
  active_companies: number;
  total_users: number;
  active_users: number;
}

// --- Query Parameters ---

export interface ListCompaniesParams {
  search?: string;
  page?: number;
  size?: number;
  include_inactive?: boolean;
}

export interface ListUsersParams {
  search?: string;
  company_id?: string;
  page?: number;
  size?: number;
  include_inactive?: boolean;
}

export interface ListEmployeesParams {
  search?: string;
  company_id?: string;
  page?: number;
  size?: number;
}

// --- Permission Constants ---

export const CompanyPermissions = {
  SUPER_ADMIN: 0,
  OWNER: 1,
  EMPLOYEE: 2,
} as const;

export type CompanyPermissionType =
  (typeof CompanyPermissions)[keyof typeof CompanyPermissions];

/**
 * App feature-access level (`feature_permissions`) — distinct from
 * `company_permissions` (the role). This is the enum the route gate
 * (`permission-check.tsx`) reads to allow/deny pages like recap. Level 3
 * unlocks everything.
 */
export const FeaturePermissions = {
  NONE: 0,
  MESSAGES: 1,
  MESSAGES_STOCKS: 2,
  ALL: 3,
} as const;

export type FeaturePermissionType =
  (typeof FeaturePermissions)[keyof typeof FeaturePermissions];

// `satisfies` enforces a label for every FeaturePermissions member at compile
// time (add one without a label → type error), while the `Record<number,…>`
// annotation keeps plain-number indexing (e.g. `user.feature_permissions`).
export const FEATURE_PERMISSION_LABELS: Record<number, string> = {
  [FeaturePermissions.NONE]: "No access",
  [FeaturePermissions.MESSAGES]: "Messages only",
  [FeaturePermissions.MESSAGES_STOCKS]: "Messages + Stocks",
  [FeaturePermissions.ALL]: "All features",
} satisfies Record<FeaturePermissionType, string>;

// --- Group Types ---
//
// Responses reuse the tenant group shape (GroupResponse / GroupMemberResponse
// from "@/app/api/types/group"). Only the superadmin-specific request bodies
// live here; SuperAdminGroupCreate carries an explicit owner_id because a super
// admin is not a member of the target company.

export interface SuperAdminGroupCreate {
  name: string;
  owner_id: string;
}

export interface SuperAdminGroupRename {
  name: string;
}

export interface SuperAdminGroupListItem {
  id: string;
  company_id: string;
  company_name: string | null;
  name: string;
  owner_id: string;
  owner_name: string | null;
  member_count: number;
}

export interface PaginatedGroupsResponse {
  items: SuperAdminGroupListItem[];
  total: number;
  page: number;
  size: number;
}

export interface ListGroupsParams {
  search?: string;
  company_id?: string;
  page?: number;
  size?: number;
}

// --- Messaging Channel Types ---
//
// Cross-tenant messaging console. Email (legacy IMAP + new SES-forward),
// WhatsApp and SMS all expose company_id / user_id / group_id, so they are
// projected into one uniform MessagingChannel shape. Assigning = setting
// user_id (owner); linking/unlinking = setting/clearing group_id.

export type MessagingChannelType = "email" | "whatsapp" | "sms";

export interface MessagingChannel {
  channel_type: MessagingChannelType;
  id: string;
  label: string;
  /** email → ingestion_mode, whatsapp/sms → phone number. */
  subtype: string | null;
  is_active: boolean;
  status: string | null;
  user_id: string;
  user_email: string | null;
  user_name: string | null;
  group_id: string | null;
  group_name: string | null;
  created_at: string | null;
}

export interface MessagingUserOption {
  id: string;
  email: string;
  full_name: string | null;
}

export interface MessagingGroupOption {
  id: string;
  name: string;
}

export interface CompanyMessagingResponse {
  company_id: string;
  company_name: string;
  channels: MessagingChannel[];
  users: MessagingUserOption[];
  groups: MessagingGroupOption[];
}

export interface ChannelAssignmentUpdate {
  user_id?: string;
  /** null unlinks from any group; omit to leave the group unchanged. */
  group_id?: string | null;
}

/** Which email system a newly-added account belongs to. */
export type EmailChannelMode = "ses_forward" | "imap_legacy";

interface ChannelCreateBase {
  /** Active user of the target company the channel is assigned to. */
  user_id: string;
  group_id?: string | null;
}

export interface EmailChannelCreate extends ChannelCreateBase {
  email_address: string;
  /** New SES-forward inbound (default) or legacy IMAP polling. */
  mode: EmailChannelMode;
  /** IMAP-only — required (with password) when mode is imap_legacy. */
  imap_server?: string | null;
  imap_port?: number;
  use_ssl?: boolean;
  password?: string | null;
}

export interface SmsChannelCreate extends ChannelCreateBase {
  name: string;
  phone_number: string;
  account_sid: string;
  auth_token: string;
}
